import { ActionsObservable, ofType, combineEpics } from 'redux-observable';
import { filter, mapTo, takeUntil, mergeMap } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';

// constants
import { WIDGET_ACTIVE_VIEW_TRANSITION, REGISTER_VIEW_TRANSITION, UNREGISTER_VIEW_TRANSITION } from './router.constants';

// types
import { IEpic } from 'types/epic';
import { IAction } from 'types/action';

interface IUnregisterEpicMethod {
    (): void;
}

interface IUnregisterEpicCreator {
    (unregisterAction$: BehaviorSubject<string>): IUnregisterEpicMethod;
}

export interface IViewTransitionEpicRegistrator {
    targetAction: string;
    targetState: string;
    currentState: string;
    optionalFilterValue?: ((action: IAction) => boolean) | string;
}

interface IRegisterEpicParams extends IViewTransitionEpicRegistrator {
    cancelationAction$: BehaviorSubject<string>;
}

export const epic$ = new BehaviorSubject(combineEpics(...[]));

export const createEmptyRouterEpics = (): IEpic => {
    return (action$, store) => epic$.pipe(mergeMap(epic => epic(action$, store, {})));
};

export const viewTransitionEpicCreator = (params: IRegisterEpicParams): IEpic => {
    const { targetAction, targetState, currentState, optionalFilterValue, cancelationAction$ } = params;

    const filterFunc = typeof optionalFilterValue === 'function' ? optionalFilterValue : () => true;

    return (action$: ActionsObservable<IAction>, state$) =>
        action$.pipe(
            ofType(targetAction),
            // turn on epic only in case if app is in specified state
            filter(() => state$.value.router.currentView === currentState),
            // perform condition checking
            filter(filterFunc),
            // logger for quick debugger purposes
            // map( item => { console.log('Transition triggered by:', currentState, targetAction); return item; }),
            mapTo({ type: WIDGET_ACTIVE_VIEW_TRANSITION, payload: targetState }),
            takeUntil(
                cancelationAction$.pipe(
                    filter(action => {
                        return action === UNREGISTER_VIEW_TRANSITION;
                    })
                )
            )
        );
};

const unregisterViewTransitionEpicCreator: IUnregisterEpicCreator = unregisterAction$ => () => {
    unregisterAction$.next(UNREGISTER_VIEW_TRANSITION);
    unregisterAction$.complete();
};

export const registerViewTransitionEpic = (params: IViewTransitionEpicRegistrator): IUnregisterEpicMethod => {
    const cancelationAction$ = new BehaviorSubject(REGISTER_VIEW_TRANSITION);
    const _epic$ = viewTransitionEpicCreator({ ...params, cancelationAction$ });
    epic$.next(_epic$);
    return unregisterViewTransitionEpicCreator(cancelationAction$);
};
